import { firstErrorCode } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import { CognitoSignInError } from './cognitoSignIn';
import { SocialProvider, socialProviderName } from './socialSignIn';

// The social sign-in codes name the provider the person used (ENG-6169), so
// their copy lives here rather than in the provider-blind default messages.
const SOCIAL_MESSAGES: Record<string, (name: string, provider: SocialProvider) => string> = {
  [ERROR_CODES.AUTH_SOCIAL_DISABLED]: (name) => `${name} sign-in isn't available right now.`,
  [ERROR_CODES.AUTH_SOCIAL_TOKEN_INVALID]: (name) =>
    `We couldn't sign you in with ${name}. Please try again.`,
  // Microsoft sends no email_verified: the api answers this when the token
  // carries no email at all.
  [ERROR_CODES.AUTH_SOCIAL_EMAIL_UNVERIFIED]: (name, provider) =>
    provider === SocialProvider.Microsoft
      ? "Your Microsoft account didn't share an email address. Please sign in another way."
      : `Your ${name} account's email isn't verified. Please sign in another way.`,
  [ERROR_CODES.AUTH_SOCIAL_IDENTITY_TAKEN]: (name) =>
    `This ${name} account is already connected to another OnFrontiers account.`,
  [ERROR_CODES.AUTH_SOCIAL_NOT_LINKED]: (name) =>
    `This ${name} account isn't connected to an OnFrontiers account. Sign in another way, then connect ${name}.`,
  [ERROR_CODES.AUTH_SOCIAL_SIGNUP_TOKEN_INVALID]: (name) =>
    `Your ${name} sign-up expired. Please continue with ${name} again.`,
  [ERROR_CODES.AUTH_SOCIAL_CONNECT_TOKEN_INVALID]: () =>
    'The connection request expired. Try again.',
  [ERROR_CODES.AUTH_SOCIAL_PROVIDER_CONNECTED]: (name) =>
    `A ${name} account is already connected to your OnFrontiers account. Disconnect it first.`,
  [ERROR_CODES.AUTH_SOCIAL_LAST_SIGN_IN_METHOD]: (name) =>
    `${name} is your only way to sign in. Set a password before disconnecting it.`,
};

// ENG-6165: the createUser refusals that end a social sign-up rather than fail
// it: the signup token lapsed, the provider account was linked meanwhile, or
// the feature is off. The form goes back to a password signup.
export const SOCIAL_SIGNUP_ENDED: ReadonlySet<string> = new Set([
  ERROR_CODES.AUTH_SOCIAL_SIGNUP_TOKEN_INVALID,
  ERROR_CODES.AUTH_SOCIAL_IDENTITY_TAKEN,
  ERROR_CODES.AUTH_SOCIAL_DISABLED,
]);

/** The message for an api error code, naming the provider for the social codes. */
export function socialErrorMessage(code: string, provider: SocialProvider): string | undefined {
  const social = SOCIAL_MESSAGES[code];
  return social ? social(socialProviderName(provider), provider) : getDefaultErrorMessage(code);
}

/**
 * What to tell the user when "Continue with Google" or "Continue with
 * Microsoft" fails (ENG-6165, ENG-6169). `expected` is false for an error with
 * no known code (a network failure, an api contract break, a Microsoft popup
 * error), which the caller reports.
 */
export function socialSignInFailure(
  err: unknown,
  provider: SocialProvider
): { message: string; expected: boolean } {
  const code = err instanceof CognitoSignInError ? err.code : firstErrorCode(err);
  const known = code ? socialErrorMessage(code, provider) : undefined;
  if (known) return { message: known, expected: true };
  return {
    message: socialErrorMessage(ERROR_CODES.AUTH_SOCIAL_TOKEN_INVALID, provider) as string,
    expected: false,
  };
}
